<template>
  <div class="bg-gray-50 min-h-full" @click="clearSelection">
    <div class="space-y-4">
      <!-- 搜索和筛选区域 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div class="p-4">
          <!-- 第一行：搜索框和主要按钮 -->
          <div class="flex flex-col sm:flex-row gap-4 mb-4">
            <!-- 搜索框 -->
            <div class="flex-1">
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索敏感词内容..."
                  class="w-full h-10 pl-10 pr-4 text-sm bg-gray-50/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 placeholder-gray-400"
                  @keyup.enter="handleSearch"
                >
                <MagnifyingGlassIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <!-- 筛选器组 -->
            <div class="flex gap-2">
              <!-- 分类筛选 -->
              <div class="flex-1 sm:w-44 sm:flex-initial">
                <SearchableSelect
                  v-model="selectedCategory"
                  :options="categoryOptions"
                  placeholder="全部分类"
                  label-key="name"
                  value-key="id"
                  @update:modelValue="handleSearch"
                />
              </div>
            </div>

            <!-- 搜索和添加按钮 -->
            <div class="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
              <button
                @click="handleSearch"
                :disabled="loading"
                class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MagnifyingGlassIcon class="h-4 w-4 mr-2" />
                搜索敏感词
              </button>
              <button
                @click="handleAddWord"
                class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200"
              >
                <PlusIcon class="h-4 w-4 mr-2" />
                添加敏感词
              </button>
            </div>
          </div>

          <!-- 第二行：管理按钮 -->
          <div class="flex flex-col sm:flex-row gap-4">
            <!-- 管理分类按钮 -->
            <div class="sm:flex sm:gap-2">
              <button
                @click="handleManageCategories"
                class="w-full sm:w-auto inline-flex items-center justify-center h-10 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
              >
                <FolderIcon class="h-4 w-4 mr-2" />
                管理分类
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 敏感词列表 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-500">加载中...</p>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else-if="words.length === 0" class="text-center py-20">
          <ShieldExclamationIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ apiError ? '加载失败' : (searchQuery || selectedCategory ? '无匹配结果' : '暂无敏感词') }}
          </h3>
          <p class="text-gray-500 mb-6">
            {{ apiError ? '请检查网络连接或稍后重试' : (searchQuery || selectedCategory ? '尝试调整搜索条件或筛选条件' : '开始添加您的第一个敏感词') }}
          </p>
          <button
            v-if="!apiError"
            @click="handleAddWord"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 transition-all duration-200"
          >
            <PlusIcon class="h-4 w-4 mr-2" />
            添加敏感词
          </button>
        </div>

        <!-- 批量操作栏 -->
        <div v-if="selectedWords.length > 0" class="bg-blue-50 px-4 sm:px-6 py-3 border-b border-gray-200">
          <!-- 桌面端：横向布局 -->
          <div class="hidden sm:flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <span class="text-sm text-gray-700">已选择 {{ selectedWords.length }} 个敏感词</span>
              <button
                type="button"
                @click="selectedWords = []"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                取消选择
              </button>
            </div>
            <div class="flex items-center space-x-2">
              <button
                type="button"
                @click="handleBulkActivate"
                class="inline-flex items-center px-3 py-1 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                启用
              </button>
              <button
                type="button"
                @click="handleBulkDeactivate"
                class="inline-flex items-center px-3 py-1 border border-orange-300 shadow-sm text-sm font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                禁用
              </button>
              <button
                type="button"
                @click="handleBulkChangeCategory"
                class="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                更改分类
              </button>
              <button
                type="button"
                @click="handleBulkDelete"
                :disabled="bulkDeleting"
                class="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="bulkDeleting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ bulkDeleting ? '删除中...' : '批量删除' }}
              </button>
            </div>
          </div>

          <!-- 手机端：纵向布局 -->
          <div class="block sm:hidden space-y-3">
            <!-- 选择信息行 -->
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700">已选择 {{ selectedWords.length }} 个敏感词</span>
              <button
                type="button"
                @click="selectedWords = []"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                取消选择
              </button>
            </div>
            
            <!-- 操作按钮列表 -->
            <div class="grid grid-cols-4 gap-1">
              <button
                type="button"
                @click="handleBulkActivate"
                class="inline-flex items-center justify-center px-1 py-2 border border-green-300 shadow-sm text-xs font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-green-500"
              >
                启用
              </button>
              <button
                type="button"
                @click="handleBulkDeactivate"
                class="inline-flex items-center justify-center px-1 py-2 border border-orange-300 shadow-sm text-xs font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-orange-500"
              >
                禁用
              </button>
              <button
                type="button"
                @click="handleBulkChangeCategory"
                class="inline-flex items-center justify-center px-1 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500"
              >
                更改分类
              </button>
              <button
                type="button"
                @click="handleBulkDelete"
                :disabled="bulkDeleting"
                class="inline-flex items-center justify-center px-1 py-2 border border-red-300 shadow-sm text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="bulkDeleting" class="animate-spin h-3 w-3 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="!bulkDeleting">删除</span>
                <span v-else class="ml-1">删除中</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 敏感词数据内容 -->
        <div v-if="!loading && words.length > 0">
          <!-- 桌面端表格视图 -->
          <div class="hidden sm:block">
            <table class="w-full table-fixed">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 0px;">
                    <input
                      type="checkbox"
                      :checked="isAllSelected"
                      :indeterminate="isIndeterminate"
                      @change="toggleSelectAll"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th class="pl-3 pr-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 160px;">
                    敏感词
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 140px;">
                    分类
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 100px;">
                    匹配方式
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 240px;">
                    替换建议
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 80px;">
                    状态
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 140px;">
                    创建时间
                  </th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 120px;">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="word in words" :key="word.id" class="hover:bg-gray-50">
                  <td class="px-4 py-4 whitespace-nowrap" style="width: 0px;">
                    <input
                      type="checkbox"
                      :value="word.id"
                      v-model="selectedWords"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td class="pl-3 pr-4 py-4 whitespace-nowrap" style="width: 160px;">
                    <div class="text-sm font-medium text-gray-900 truncate">{{ word.word }}</div>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap" style="width: 140px;">
                    <span 
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getCategoryBadgeClass(word.category_severity)"
                    >
                      {{ word.category_name }}
                    </span>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap" style="width: 100px;">
                    <span class="text-sm text-gray-500">
                      {{ getMatchTypeLabel(word.match_type) }}
                    </span>
                  </td>
                  <td class="px-4 py-4" style="width: 240px;">
                    <div v-if="word.alternative_words_list && word.alternative_words_list.length > 0" class="flex flex-wrap gap-1">
                      <span
                        v-for="alt in word.alternative_words_list.slice(0, 3)"
                        :key="alt"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {{ alt }}
                      </span>
                      <span v-if="word.alternative_words_list.length > 3" class="text-xs text-gray-400">
                        +{{ word.alternative_words_list.length - 3 }}
                      </span>
                    </div>
                    <span v-else class="text-sm text-gray-400">无</span>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap" style="width: 80px;">
                    <span 
                      :class="word.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    >
                      {{ word.is_active ? '启用' : '禁用' }}
                    </span>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500" style="width: 140px;">
                    {{ formatDate(word.created_at) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm font-medium" style="width: 120px;">
                    <div class="flex items-center space-x-2">
                      <!-- 编辑 -->
                      <button
                        @click="handleEditWord(word)"
                        class="inline-flex items-center text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-xs transition-colors"
                        title="编辑敏感词"
                      >
                        <PencilIcon class="h-4 w-4 mr-1" />
                        编辑
                      </button>

                      <!-- 删除 -->
                      <button
                        @click="handleDeleteWord(word)"
                        :disabled="word.deleting"
                        class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="删除敏感词"
                      >
                        <TrashIcon v-if="!word.deleting" class="h-4 w-4 mr-1" />
                        <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ word.deleting ? '删除中...' : '删除' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 手机端卡片视图 -->
          <div class="block sm:hidden space-y-4">
            <!-- 全选功能 -->
            <div class="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
              <div class="flex items-center space-x-3">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate="isIndeterminate"
                  @change="toggleSelectAll"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span class="text-sm text-gray-700">全选</span>
              </div>
              <span class="text-sm text-gray-500">
                {{ selectedWords.length > 0 ? `已选择 ${selectedWords.length} 个` : `共 ${words.length} 个敏感词` }}
              </span>
            </div>

            <!-- 敏感词卡片列表 -->
            <div
              v-for="word in words"
              :key="word.id"
              class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <!-- 卡片头部 -->
              <div class="p-4 border-b border-gray-100">
                <div class="flex items-start justify-between">
                  <div class="flex items-start space-x-3 flex-1">
                    <!-- 选择复选框 -->
                    <input
                      type="checkbox"
                      :value="word.id"
                      v-model="selectedWords"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                    />
                    <!-- 敏感词基本信息 -->
                    <div class="flex-1 min-w-0">
                      <h3 class="text-base font-medium text-gray-900">{{ word.word }}</h3>
                      <div class="flex flex-wrap gap-2 mt-2">
                        <span 
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="getCategoryBadgeClass(word.category_severity)"
                        >
                          {{ word.category_name }}
                        </span>
                        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {{ getMatchTypeLabel(word.match_type) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <!-- 状态和时间 -->
                  <div class="text-right ml-2">
                    <span 
                      :class="word.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mb-1"
                    >
                      {{ word.is_active ? '启用' : '禁用' }}
                    </span>
                    <div class="text-xs text-gray-400">
                      {{ formatDate(word.created_at).split(' ')[0] }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- 卡片内容 -->
              <div class="p-4 space-y-3">
                <!-- 替换建议 -->
                <div v-if="word.alternative_words_list && word.alternative_words_list.length > 0">
                  <div class="flex items-center space-x-2 mb-2">
                    <span class="text-xs font-medium text-gray-500">替换建议:</span>
                  </div>
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="alt in word.alternative_words_list"
                      :key="alt"
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {{ alt }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 卡片底部操作 -->
              <div class="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg">
                <div class="flex items-center justify-between space-x-2">
                  <div class="flex items-center space-x-2">
                    <!-- 编辑 -->
                    <button
                      @click="handleEditWord(word)"
                      class="inline-flex items-center text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 px-3 py-2 rounded-md text-xs font-medium transition-colors border border-gray-200"
                      title="编辑敏感词"
                    >
                      <PencilIcon class="h-4 w-4 mr-1" />
                      编辑
                    </button>
                  </div>

                  <!-- 删除 -->
                  <button
                    @click="handleDeleteWord(word)"
                    :disabled="word.deleting"
                    class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-md text-xs font-medium transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="删除敏感词"
                  >
                    <TrashIcon v-if="!word.deleting" class="h-4 w-4 mr-1" />
                    <svg v-else class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ word.deleting ? '删除中...' : '删除' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="words.length > 0" class="bg-white border-t border-gray-200">
          <!-- 移动端分页：简化版本 -->
          <div class="px-4 py-3 sm:hidden">
            <!-- 分页按钮和每页数量选择器 -->
            <div class="flex justify-between items-center">
              <button
                @click="prevPage"
                :disabled="currentPage === 1 || loading"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                上一页
              </button>
              
              <!-- 每页数量选择器 -->
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-700">每页</span>
                <select
                  v-model="pageSize"
                  @change="handlePageSizeChange"
                  class="border border-gray-300 rounded-md px-3 py-1 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px] w-auto"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="text-sm text-gray-700">条</span>
              </div>
              
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages || loading"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
          
          <!-- 桌面端分页：显示完整信息 -->
          <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between px-6 py-3">
            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-700">
                显示第 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalWords) }} 条，
                共 {{ totalWords }} 条记录
              </span>
              
              <!-- 每页数量选择器 -->
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-700">每页</span>
                <select
                  v-model="pageSize"
                  @change="handlePageSizeChange"
                  class="border border-gray-300 rounded-md px-3 py-1 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[60px] w-auto"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span class="text-sm text-gray-700">条</span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="prevPage"
                :disabled="currentPage === 1 || loading"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon class="h-4 w-4" />
                上一页
              </button>
              <span class="px-3 py-2 text-sm font-medium text-gray-700">
                第 {{ currentPage }} / {{ totalPages }} 页
              </span>
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages || loading"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
                <ChevronRightIcon class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分类管理模态框 -->
    <SensitiveWordCategoryModal 
      :show="showCategoryModal"
      @close="closeCategoryModal"
      @success="handleCategorySuccess"
    />

    <!-- 敏感词添加/编辑模态框 -->
    <SensitiveWordModal 
      :show="showWordModal"
      :word="editingWord"
      :categories="categories"
      @close="closeWordModal"
      @success="handleWordSuccess"
    />


    <!-- 批量更改分类模态框 -->
    <BulkChangeCategoryModal 
      :show="showBulkCategoryModal"
      :categories="categories"
      :selected-count="selectedWords.length"
      @close="showBulkCategoryModal = false"
      @confirm="handleConfirmBulkChangeCategory"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import { sensitiveWordService, sensitiveWordCategoryService } from '@/services/sensitiveWordService'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import SensitiveWordCategoryModal from '@/components/sensitive-words/SensitiveWordCategoryModal.vue'
import SensitiveWordModal from '@/components/sensitive-words/SensitiveWordModal.vue'
import BulkChangeCategoryModal from '@/components/sensitive-words/BulkChangeCategoryModal.vue'
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  ShieldExclamationIcon,
  PencilIcon, 
  TrashIcon, 
  FolderIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/vue/24/outline'

const toast = useToast()

// 响应式数据
const searchQuery = ref('')
const selectedCategory = ref('')
const currentPage = ref(1)
const pageSize = ref(20)
const words = ref([])
const categories = ref([])
const loading = ref(true)
const totalWords = ref(0)
const apiError = ref('')

// 批量操作
const selectedWords = ref([])
const bulkDeleting = ref(false)

// 模态框状态
const showCategoryModal = ref(false)
const showWordModal = ref(false)
const showBulkCategoryModal = ref(false)
const editingWord = ref(null)

// 计算属性
const totalPages = computed(() => Math.ceil(totalWords.value / pageSize.value))

// 批量操作计算属性
const isAllSelected = computed(() => {
  return words.value.length > 0 && selectedWords.value.length === words.value.length
})

const isIndeterminate = computed(() => {
  return selectedWords.value.length > 0 && selectedWords.value.length < words.value.length
})

// 分类选项格式化
const categoryOptions = computed(() => {
  return [
    { id: '', name: '全部分类' },
    ...categories.value.map(category => ({ id: category.id, name: category.name }))
  ]
})

// 方法
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedWords.value = []
  } else {
    selectedWords.value = words.value.map(word => word.id)
  }
}

const clearSelection = () => {
  // 点击页面其他地方时清空选择（如果需要的话）
}

const getCategoryBadgeClass = (severity) => {
  switch (severity) {
    case 'forbidden':
      return 'bg-red-100 text-red-800'
    case 'high':
      return 'bg-orange-100 text-orange-800'
    case 'medium':
      return 'bg-yellow-100 text-yellow-800'
    case 'low':
      return 'bg-blue-100 text-blue-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getMatchTypeLabel = (matchType) => {
  switch (matchType) {
    case 'exact':
      return '精确匹配'
    case 'contains':
      return '包含匹配'
    case 'regex':
      return '正则匹配'
    default:
      return '未知'
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-')
}

// 搜索和筛选
const handleSearch = async () => {
  window.scrollTo({ top: 0, behavior: 'instant' })
  currentPage.value = 1
  selectedWords.value = []
  await loadSensitiveWords()
}

// 加载敏感词列表
const loadSensitiveWords = async () => {
  loading.value = true
  apiError.value = ''
  
  try {
    const params = {
      page: currentPage.value,
      page_size: pageSize.value
    }
    
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim()
    }
    
    if (selectedCategory.value) {
      params.category = selectedCategory.value
    }
    
    
    const result = await sensitiveWordService.getSensitiveWords(params)
    
    // 直接处理 API 返回的数据（分页格式）
    words.value = result.results || result.data || []
    totalWords.value = result.count || 0
    
  } catch (error) {
    console.error('加载敏感词失败:', error)
    apiError.value = error.message || '加载敏感词列表失败，请稍后重试'
    words.value = []
    totalWords.value = 0
  } finally {
    loading.value = false
  }
}

// 加载分类列表
const loadCategories = async () => {
  try {
    const result = await sensitiveWordCategoryService.getCategories()
    categories.value = result.results || result.data || []
  } catch (error) {
    console.error('加载分类失败:', error)
  }
}

// 分页方法
const prevPage = async () => {
  if (currentPage.value > 1) {
    window.scrollTo({ top: 0, behavior: 'instant' })
    currentPage.value--
    selectedWords.value = []
    await loadSensitiveWords()
  }
}

const nextPage = async () => {
  if (currentPage.value < totalPages.value) {
    window.scrollTo({ top: 0, behavior: 'instant' })
    currentPage.value++
    selectedWords.value = []
    await loadSensitiveWords()
  }
}

const handlePageSizeChange = async () => {
  pageSize.value = Number(pageSize.value)
  window.scrollTo({ top: 0, behavior: 'instant' })
  currentPage.value = 1
  selectedWords.value = []
  await loadSensitiveWords()
}

// 事件处理
const handleAddWord = () => {
  editingWord.value = null
  showWordModal.value = true
}

const handleEditWord = (word) => {
  editingWord.value = word
  showWordModal.value = true
}

const handleDeleteWord = async (word) => {
  if (!confirm(`确定要删除敏感词"${word.word}"吗？`)) {
    return
  }

  word.deleting = true
  try {
    await sensitiveWordService.deleteSensitiveWord(word.id)
    toast.success('敏感词删除成功')
    await loadSensitiveWords()
  } catch (error) {
    console.error('删除敏感词失败:', error)
    toast.error(error.response?.data?.message || error.message || '删除失败，请稍后重试')
  } finally {
    word.deleting = false
  }
}

const handleManageCategories = () => {
  showCategoryModal.value = true
}


// 批量操作
const handleBulkActivate = async () => {
  if (selectedWords.value.length === 0) {
    toast.warning('请选择要启用的敏感词')
    return
  }

  try {
    await sensitiveWordService.bulkOperation({
      word_ids: selectedWords.value,
      action: 'activate'
    })

    toast.success(`成功启用 ${selectedWords.value.length} 个敏感词`)
    selectedWords.value = []
    await loadSensitiveWords()
  } catch (error) {
    console.error('批量启用失败:', error)
    toast.error(error.response?.data?.message || error.message || '批量启用失败，请稍后重试')
  }
}

const handleBulkDeactivate = async () => {
  if (selectedWords.value.length === 0) {
    toast.warning('请选择要禁用的敏感词')
    return
  }

  try {
    await sensitiveWordService.bulkOperation({
      word_ids: selectedWords.value,
      action: 'deactivate'
    })

    toast.success(`成功禁用 ${selectedWords.value.length} 个敏感词`)
    selectedWords.value = []
    await loadSensitiveWords()
  } catch (error) {
    console.error('批量禁用失败:', error)
    toast.error(error.response?.data?.message || error.message || '批量禁用失败，请稍后重试')
  }
}

const handleBulkChangeCategory = () => {
  if (selectedWords.value.length === 0) {
    toast.warning('请选择要更改分类的敏感词')
    return
  }
  showBulkCategoryModal.value = true
}

const handleConfirmBulkChangeCategory = async (categoryId) => {
  try {
    await sensitiveWordService.bulkOperation({
      word_ids: selectedWords.value,
      action: 'change_category',
      category_id: categoryId
    })

    toast.success(`成功更改 ${selectedWords.value.length} 个敏感词的分类`)
    selectedWords.value = []
    await loadSensitiveWords()
  } catch (error) {
    console.error('批量更改分类失败:', error)
    toast.error(error.response?.data?.message || error.message || '批量更改分类失败，请稍后重试')
  }
}

const handleBulkDelete = async () => {
  if (selectedWords.value.length === 0) {
    toast.warning('请选择要删除的敏感词')
    return
  }

  if (!confirm(`确定要删除选中的 ${selectedWords.value.length} 个敏感词吗？此操作无法撤销。`)) {
    return
  }

  bulkDeleting.value = true
  try {
    await sensitiveWordService.bulkOperation({
      word_ids: selectedWords.value,
      action: 'delete'
    })

    toast.success(`成功删除 ${selectedWords.value.length} 个敏感词`)
    selectedWords.value = []
    await loadSensitiveWords()
    
    // 如果当前页没有数据了，且不是第一页，回到上一页
    if (words.value.length === 0 && currentPage.value > 1) {
      currentPage.value--
      await loadSensitiveWords()
    }
  } catch (error) {
    console.error('批量删除失败:', error)
    toast.error(error.response?.data?.message || error.message || '批量删除失败，请稍后重试')
  } finally {
    bulkDeleting.value = false
  }
}

// 模态框处理
const closeCategoryModal = () => {
  showCategoryModal.value = false
}

const handleCategorySuccess = async () => {
  await loadCategories()
  await loadSensitiveWords()
}

const closeWordModal = () => {
  showWordModal.value = false
  editingWord.value = null
}

const handleWordSuccess = async (result) => {
  if (result && result.data && editingWord.value) {
    // 编辑模式：使用返回的数据静默更新列表，补充关联字段信息
    const updatedWord = result.data
    const wordIndex = words.value.findIndex(w => w.id === editingWord.value.id)
    
    if (wordIndex !== -1) {
      // 找到对应的分类信息
      const category = categories.value.find(cat => cat.id === updatedWord.category)
      
      // 获取匹配类型显示文本
      const matchTypeDisplay = getMatchTypeDisplay(updatedWord.match_type)
      
      // 处理替换建议字段
      let alternativeWordsList = []
      if (updatedWord.alternative_words_list) {
        alternativeWordsList = updatedWord.alternative_words_list
      } else if (updatedWord.alternative_words && updatedWord.alternative_words.trim()) {
        // 如果后端返回的是字符串格式，需要转换为数组
        alternativeWordsList = updatedWord.alternative_words.split(',')
          .map(word => word.trim())
          .filter(word => word.length > 0)
      }
      
      // 静默更新列表项，保持原有ID和时间戳，补充关联字段
      words.value[wordIndex] = {
        ...words.value[wordIndex], // 保持原有的id、created_at、updated_at等
        ...updatedWord, // 更新的基本字段
        category_name: category?.name || '未知分类',
        category_severity: category?.severity_level || 'medium',
        match_type_display: matchTypeDisplay,
        alternative_words_list: alternativeWordsList
      }
    }
  } else {
    // 新建模式：重新加载列表
    await loadSensitiveWords()
  }
}

// 获取匹配类型显示文本的辅助函数
const getMatchTypeDisplay = (matchType) => {
  const matchTypeMap = {
    'exact': '精确匹配',
    'contains': '包含匹配', 
    'regex': '正则匹配'
  }
  return matchTypeMap[matchType] || matchType
}


// 组件挂载时加载数据
onMounted(async () => {
  await loadCategories()
  await loadSensitiveWords()
})
</script>

<style scoped>
/* 保持与CustomerList.vue一致的样式 */
</style>
